<template>
  <div v-if="$apollo.loading && !showMoreLoading">
    <div class="w-full h-[400px] bg-gray-lightest animate-pulse" role="status">
      <span class="sr-only">Loading...</span>
    </div>

    <div class="container">
      <BreadcrumbsSkeleton class="my-10" />

      <TabsSkeleton class="my-10" />

      <ParagraphSkeleton class="my-10" />

      <div
        class="space-y-6 md:grid md:gap-16 lg:grid-cols-4 md:grid-cols-2 md:space-y-0"
      >
        <PostCardSkeleton
          v-for="n in 3"
          :key="n"
          :class="{ 'md:col-span-2': n === 1 }"
        />

        <ActionSkeleton class="col-span-full" />
      </div>
    </div>
  </div>
  <div v-else>
    <VisualHero preload background-color="bg-orange-news" :image="visualImage">
      <template slot="title">{{ title }}</template>
      <template slot="description">{{ visualSubtitle }}</template>
    </VisualHero>

    <div class="container mx-auto">
      <div class="flex my-10">
        <Breadcrumbs v-if="category" :items="breadcrumbItems" />
      </div>

      <div
        class="flex flex-row my-10 overflow-x-scroll flex-nowrap whitespace-nowrap"
      >
        <div
          v-for="(cat, key) in featuredCategories"
          :key="key"
          class="flex flex-col flex-1 w-full border-b group border-b-gray-lightest"
        >
          <NuxtLink
            v-if="$route.params.category !== cat.slug"
            :key="key"
            :to="cat.url"
            class="w-full py-[8px] px-[20px] font-bold group-hover:text-blue-dark text-blue text-subtitle uppercase border-b-2 border-b-transparent group-hover:border-b-blue"
          >
            {{ cat.label }}
          </NuxtLink>
          <button
            v-else
            type="button"
            class="text-left w-full py-[8px] px-[20px] cursor-default font-bold text-blue-dark text-subtitle uppercase border-b-2 border-b-blue"
          >
            {{ cat.label }}
          </button>
        </div>
      </div>

      <div class="my-10">
        <p class="text-center">{{ description }}</p>
      </div>

      <div
        class="space-y-6 md:grid md:gap-16 lg:grid-cols-4 md:grid-cols-2 md:space-y-0"
      >
        <template v-for="({ node: post, cursor }, n) in getFirstTreePosts">
          <PostCard
            :key="cursor"
            :class="{ 'md:col-span-2': n === 0 }"
            :post="post"
            :show-category="false"
            type="vertical"
          />
        </template>

        <Action v-bind="actionBanner" size="wide" class="col-span-full">
          <template slot="icon">
            <SvgIcon
              name="bank-accounts"
              aria-hidden="true"
              class="max-w-12 max-h-12 mr-8"
            />
          </template>
        </Action>

        <template v-for="{ node: post, cursor } in getPostsAfterTree">
          <PostCard
            :key="cursor"
            :post="post"
            :show-category="false"
            type="vertical"
          />
        </template>

        <template v-if="$apollo.queries.category.loading">
          <PostCardSkeleton v-for="n in 8" :key="n" />
        </template>
      </div>

      <div v-if="posts" class="flex flex-col items-center w-full my-10">
        <p class="font-bold uppercase font-xl">
          {{ posts.edges.length }}/{{ posts.pageInfo.total }}
        </p>
        <button
          type="button"
          class="flex flex-row py-[13px] px-8 rounded font-bold text-subtitle items-center"
          :class="[
            {
              'bg-blue hover:bg-blue-dark text-white':
                posts.pageInfo.hasNextPage,
              'bg-gray-lightest text-gray-light cursor-default': !posts.pageInfo
                .hasNextPage,
            },
          ]"
          @click="showMore"
        >
          Mostra di più
        </button>
      </div>

      <Action
        title="Numero verde"
        content="Da telefono fisso e cellulare 800.89.69.68 (dall'estero +39 02 84251701), attivo dal lunedì al venerdì dalle 9.00 alle 17.00"
        class="my-9.6"
      >
        <template #icon>
          <SvgIcon aria-hidden="true" name="phone" class="h-9.6 w-9.6" />
        </template>
      </Action>
    </div>
  </div>
</template>

<script>
// GraphQL
import settings from '~/apollo/queries/settings.gql'
import category from '~/apollo/queries/category.gql'

// Components
import Action from '~/components/News/Action.vue'
import ActionSkeleton from '~/components/News/ActionSkeleton.vue'
import Breadcrumbs from '~/components/News/Breadcrumbs.vue'
import BreadcrumbsSkeleton from '~/components/News/BreadcrumbsSkeleton.vue'
import ParagraphSkeleton from '~/components/News/ParagraphSkeleton.vue'
import PostCard from '~/components/News/PostCard.vue'
import PostCardSkeleton from '~/components/News/PostCardSkeleton.vue'
import TabsSkeleton from '~/components/News/TabsSkeleton.vue'
import VisualHero from '~/components/VisualHero.vue'

export default {
  components: {
    Action,
    ActionSkeleton,
    Breadcrumbs,
    BreadcrumbsSkeleton,
    ParagraphSkeleton,
    PostCard,
    PostCardSkeleton,
    TabsSkeleton,
    VisualHero,
  },
  apollo: {
    settings: {
      query: settings,
      prefetch: true,
      update(data) {
        return data
      },
    },
    category: {
      query: category,
      prefetch: ({ route }) => ({ id: route.params.category }),
      variables() {
        return { id: this.$route.params.category }
      },
      update(data) {
        if (!data?.category) {
          this.$nuxt.error({ statusCode: 404, message: 'Page not found' })
        }
        return data?.category
      },
    },
  },
  filters: {
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
      return new Date(date).toLocaleDateString('it-IT', options)
    },
  },
  data() {
    return {
      showMoreLoading: false,
    }
  },
  head() {
    return {
      title: this.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.category?.seo?.metaDesc ?? this.description,
        },
        {
          hid: 'robots',
          name: 'robots',
          content: [
            this.category?.seo?.metaRobotsNoindex ?? 'index',
            this.category?.seo?.metaRobotsNofollow ?? 'follow',
          ].join(','),
        },
        {
          hid: 'og:type',
          name: 'og:type',
          content: 'object',
        },
        {
          hid: 'og:title',
          name: 'og:title',
          content: this.category?.seo?.opengraphTitle,
          skip: !this.category?.seo?.opengraphTitle,
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.category?.seo?.opengraphDescription,
          skip: !this.category?.seo?.opengraphDescription,
        },
        {
          hid: 'og:url',
          name: 'og:url',
          content: this.category?.seo?.opengraphUrl,
          skip: !this.category?.seo?.opengraphUrl,
        },
        {
          hid: 'og:site_name',
          name: 'og:site_name',
          content: this.category?.seo?.opengraphSiteName,
          skip: !this.category?.seo?.opengraphSiteName,
        },
      ],
      script: [
        {
          type: 'application/ld+json',
          json: this.schema,
        },
        {
          type: 'text/javascript',
          innerHTML: `var utag_data = ${JSON.stringify({
            site_name: this.settings?.generalSettings?.title,
            site_description: this.settings?.generalSettings?.description ?? '',
            page_type: 'category-archive',
            post_title: `Category archive: ${this.title}`,
            user_role: 'guest',
          })}`,
        },
      ],
      __dangerouslyDisableSanitizers: ['innerHTML'],
    }
  },
  computed: {
    options() {
      return this.settings?.acfOptions?.acfOptions
    },
    posts() {
      return this.category?.posts
    },
    getFirstTreePosts() {
      return this.posts?.edges.slice(0, 3).map(({ node: post, cursor }) => ({
        cursor,
        node: this.$utilities.mapPost(post),
      }))
    },
    getPostsAfterTree() {
      return this.posts?.edges.slice(3).map(({ node: post, cursor }) => ({
        cursor,
        node: this.$utilities.mapPost(post),
      }))
    },
    computedPosts() {
      return this.posts?.edges.map(({ node: post, cursor }) => ({
        cursor,
        node: this.$utilities.mapPost(post),
      }))
    },
    featuredCategories() {
      return this.options?.categorieInEvidenza.map(({ categoria }) => ({
        label: categoria.name,
        url: `/news${categoria.uri}`,
        slug: categoria.slug,
      }))
    },
    title() {
      return this.category?.name
    },
    visualSubtitle() {
      return this.category?.acfCategory?.sottotitolo
    },

    visualImage() {
      if (!this.category?.acfCommon?.immagineInTestata.mediaItemUrl) {
        return
      }

      const image = this.$utilities.imgAlias(
        this.category?.acfCommon?.immagineInTestata.mediaItemUrl
      )
      return image
    },

    visualPosition() {
      return this.category?.acfCommon?.posizioneImmagineInTestata
    },
    breadcrumbItems() {
      return this.category?.seo.breadcrumbs.map((item) => ({
        text: item.text,
        url: new URL(item.url).pathname,
      }))
    },
    seoTitle() {
      return this.category?.seo.title
    },
    description() {
      return this.category?.description
    },
    schema() {
      return this.category && JSON.parse(this.category.seo.schema.raw)
    },
    actionBanner() {
      return {
        title: this.options?.disclaimerArticoli.messaggio,
        showButton: true,
        actionText: this.options?.disclaimerArticoli.linkBottone.title,
        handleAction: () => {
          const url = this.options?.disclaimerArticoli.linkBottone.url
          const relativeUrl = this.$utilities.getRelativeUrl(url)
          this.$router.push(relativeUrl)
        },
      }
    },
  },
  methods: {
    showMore() {
      this.showMoreLoading = true
      this.$apollo.queries.category.fetchMore({
        variables: {
          id: this.$route.params.category,
          first: 8,
          after: this.category.posts.pageInfo.endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          this.showMoreLoading = false
          const posts = fetchMoreResult.category?.posts.edges
          const pageInfo = fetchMoreResult.category?.posts.pageInfo
          return {
            ...previousResult,
            category: {
              ...previousResult.category,
              posts: {
                __typename: previousResult.category.posts.__typename,
                // Merging the tag list
                edges: [...previousResult.category.posts.edges, ...posts],
                pageInfo: {
                  ...previousResult.category.posts.pageInfo,
                  ...{
                    endCursor: pageInfo.endCursor,
                    hasNextPage: pageInfo.hasNextPage,
                    hasPreviousPage: pageInfo.hasPreviousPage,
                  },
                },
              },
            },
          }
        },
      })
    },
  },
}
</script>
